/**
 * Hotel store (Zustand).
 * Tracks the currently selected hotel and the list of hotels available to the user.
 * Most data-fetching hooks use the selectedHotelId from this store.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Hotel } from "@/types/hotel";

interface HotelState {
  selectedHotelId: string | null;
  hotels: Hotel[];

  setSelectedHotel: (hotelId: string) => void;
  setHotels: (hotels: Hotel[]) => void;
  getSelectedHotel: () => Hotel | null;
  removeHotel: (hotelId: string) => void;
}

export const useHotelStore = create<HotelState>()(
  persist(
    (set, get) => ({
      selectedHotelId: null,
      hotels: [],

      setSelectedHotel: (hotelId) => set({ selectedHotelId: hotelId }),

      setHotels: (hotels) => {
        const current = get().selectedHotelId;
        // Auto-select first hotel if none selected or current is no longer available
        const validId = hotels.find((h) => h.id === current)
          ? current
          : hotels[0]?.id ?? null;
        set({ hotels, selectedHotelId: validId });
      },

      removeHotel: (hotelId) => {
        const remaining = get().hotels.filter((h) => h.id !== hotelId);
        const newSelected =
          get().selectedHotelId === hotelId
            ? remaining[0]?.id ?? null
            : get().selectedHotelId;
        set({ hotels: remaining, selectedHotelId: newSelected });
      },

      getSelectedHotel: () => {
        const { hotels, selectedHotelId } = get();
        return hotels.find((h) => h.id === selectedHotelId) ?? null;
      },
    }),
    {
      name: "rms-hotel",
      partialize: (state) => ({ selectedHotelId: state.selectedHotelId }),
    }
  )
);
